import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  Users,
  CheckCircle,
  Clock,
  Upload,
  Download,
  Save,
  AlertCircle,
  Calendar as CalendarIcon,
} from "lucide-react";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { GenericTable } from "@shared/components/GenericTable";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Input } from "@shared/ui/input";
import { toast } from "@shared/ui/use-toast";
import { TableColumn } from "@shared/types/table";
import { PageHeader } from "@shared/components/PageHeader";
import { useParams } from "react-router-dom";
import {
  useDownloadMeterCSV,
  useBulkRequestMeterList,
  useUploadMeterCSV,
  useSaveMeterReading,
} from "@features/cx/disconnect/hooks";
import { useActivateBulkReconnection } from "@features/cx/reconnect/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { MODAL_PAGE_SIZE, PAGE_SIZE } from "@shared/utils/constants";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { normalizeDate } from "@shared/utils/dateUtils";
import { useForm } from "react-hook-form";
import { MeterActivationFilter } from "./MeterActivationFilter";
import { GenericSelect } from "@shared/components/Select";
import { useMeterReadingStatus } from "@features/cx/transfer/hooks";

const MeterActivationTab = () => {
  const { id } = useParams<{ id: string }>();
  const remoteUtilityId = useRemoteUtilityId();

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const filterForm = useForm({
    defaultValues: {
      category: "",
      sub_category: "",
      accountType: "",
    },
  });

  const [appliedFilters, setAppliedFilters] = useState({
    category: "",
    sub_category: "",
    accountType: "",
  });

  // Fetch meters with server-side pagination, searching, and filtering
  const { data: meterListResponse, isLoading: isMeterListLoading } =
    useBulkRequestMeterList({
      batchId: id || "",
      remoteUtilityId: Number(remoteUtilityId),
      page: page,
      limit: MODAL_PAGE_SIZE,
      searchData: searchTerm,
      category: appliedFilters.category,
      subCategory: appliedFilters.sub_category,
      accountType: appliedFilters.accountType,
    });

  const { data: meterReadingStatusData } = useMeterReadingStatus();

  const meterReadingStatusOptions = useMemo(() => {
    return (
      meterReadingStatusData?.map((item: any) => ({
        label: item.value,
        value: item.value,
      })) || []
    );
  }, [meterReadingStatusData]);

  const { mutateAsync: downloadMeterCSV, isPending: isDownloading } =
    useDownloadMeterCSV();

  const { mutateAsync: uploadMeterCSV, isPending: isUploadingCSV } =
    useUploadMeterCSV();
  const { mutateAsync: saveMeterReading } = useSaveMeterReading();
  const { mutateAsync: activateBulkReconnection, isPending: isActivating } =
    useActivateBulkReconnection();

  const [savingId, setSavingId] = useState<string | null>(null);
  const [localData, setLocalData] = useState<any[]>([]);
  const [uploadErrorReport, setUploadErrorReport] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Inline editing state
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingRowData, setEditingRowData] = useState<any | null>(null);

  const summaryData = meterListResponse?.summary || {
    totalConsumers: 0,
    totalReadingCollected: 0,
    totalReadingPending: 0,
  };

  const allMetersAssigned = useMemo(() => {
    if (!summaryData || summaryData.totalConsumers === 0) return false;
    return summaryData.totalReadingCollected === summaryData.totalConsumers;
  }, [summaryData]);

  const handleActivateConsumer = async () => {
    if (!id) return;
    try {
      await activateBulkReconnection(id);
    } catch (error) {
      // Error handled by hook
    }
  };

  useEffect(() => {
    if (meterListResponse?.results) {
      const uniqueDataMap = new Map();
      meterListResponse.results.forEach((item: any) => {
        const uniqueId = item.meterDetails?.id || item.remoteMeterId;
        if (uniqueId && !uniqueDataMap.has(uniqueId)) {
          const currentReadingVal =
            item.currentReading ??
            item.meterDetails?.currentReading ??
            "";

          const readingDateVal =
            item.readingDate ??
            item.meterDetails?.currentReadingDate ??
            "";

          uniqueDataMap.set(uniqueId, {
            ...item,
            id: uniqueId,
            currentReading: currentReadingVal,
            readingDate: readingDateVal,
          });
        }
      });
      setLocalData(Array.from(uniqueDataMap.values()));
    }
  }, [meterListResponse]);

  // Server-side pagination & stats
  const totalCount = meterListResponse?.count || 0;
  const totalPages = Math.ceil(totalCount / MODAL_PAGE_SIZE);

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems: totalCount,
      itemsPerPage: MODAL_PAGE_SIZE,
      startItem: totalCount === 0 ? 0 : (page - 1) * MODAL_PAGE_SIZE + 1,
      endItem: Math.min(page * MODAL_PAGE_SIZE, totalCount),
    }),
    [page, totalPages, totalCount],
  );

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const handleApplyFilters = (data: any) => {
    setAppliedFilters({
      category: data.category || "",
      sub_category: data.sub_category || "",
      accountType: data.accountType || "",
    });
    setPage(1);
  };

  const handleResetFilters = () => {
    setAppliedFilters({
      category: "",
      sub_category: "",
      accountType: "",
    });
    filterForm.reset({
      category: "",
      sub_category: "",
      accountType: "",
    });
    setPage(1);
  };

  // Inline editing handlers
  const handleStartEdit = (row: any) => {
    setEditingRowId(row.id);
    setEditingRowData(JSON.parse(JSON.stringify(row))); // Deep clone
  };

  const handleCancelInline = () => {
    setEditingRowId(null);
    setEditingRowData(null);
  };

  const handleReadingStatusChange = (selectedKey: string) => {
    const match = meterReadingStatusData?.find((item: any) => item.key === selectedKey);
    const displayVal = match ? match.value : selectedKey;
    setEditingRowData((prev: any) => ({
      ...prev,
      meterDetails: {
        ...prev.meterDetails,
        currentReadingMeterStatus: selectedKey,
        currentReadingMeterStatusDisplay: displayVal,
      },
    }));
  };

  const handleSaveInline = async () => {
    if (!editingRowData) return;
    const id = editingRowData.id;

    if (!editingRowData.currentReading) {
      toast({
        title: "Warning",
        description: "Please enter a reading value before saving.",
        variant: "destructive",
      });
      return;
    }

    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      consumer_no: editingRowData.consumerNo,
      consumer_name: `${editingRowData.firstName || ""} ${editingRowData.lastName || ""}`.trim(),
      meter_no: editingRowData.meterDetails?.meterNumber,
      device_no: editingRowData.meterDetails?.deviceNo,
      read: editingRowData.currentReading,
      read_date: normalizeDate(editingRowData.readingDate || ""),
      read_status: (
        editingRowData.meterDetails?.currentReadingMeterStatusDisplay ||
        editingRowData.meterDetails?.currentReadingMeterStatus ||
        "NORMAL"
      ).toUpperCase(),
    };

    setSavingId(id);
    try {
      await saveMeterReading(payload);

      setLocalData((prev) =>
        prev.map((item) => {
          if (item.id === id) {
            return {
              ...item,
              currentReading: editingRowData.currentReading,
              readingDate: editingRowData.readingDate,
              status: "Collected",
              meterDetails: {
                ...item.meterDetails,
                deviceNo: editingRowData.meterDetails?.deviceNo,
                currentReadingMeterStatus: editingRowData.meterDetails?.currentReadingMeterStatus,
                currentReadingMeterStatusDisplay: editingRowData.meterDetails?.currentReadingMeterStatusDisplay,
                status: "Assigned",
              },
            };
          }
          return item;
        })
      );

      setEditingRowId(null);
      setEditingRowData(null);
    } catch (error) {
      // Error handled by hook
    } finally {
      setSavingId(null);
    }
  };

  const handleDownload = async () => {
    if (!id) return;
    try {
      await downloadMeterCSV(id);
    } catch (error) {
      // Error handled by hook
    }
  };

  const handleUpload = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file && id) {
      try {
        const response = await uploadMeterCSV({ batchId: id, file });
        if (response?.error_count > 0) {
          setUploadErrorReport(response);
        }
        if (fileInputRef.current) fileInputRef.current.value = "";
      } catch (error) {
        // Error handled by hook
      }
    }
  };

  const columns: TableColumn<any>[] = [
    {
      key: "consumerNo",
      header: "Account No",
    },
    {
      key: "firstName",
      header: "Consumer Name",
      render: (val: any, row: any) => {
        const fullName = `${val || ""} ${row.lastName || ""}`.trim();
        return <span>{fullName || "N/A"}</span>;
      },
    },
    {
      key: "categoryDisplay",
      header: "Category",
      render: (val: any, row: any) => {
        return <span>{row.meterDetails?.categoryDisplay || "N/A"}</span>;
      },
    },
    {
      key: "subCategoryDisplay",
      header: "Sub Category",
      render: (val: any, row: any) => {
        return <span>{row.meterDetails.subCategoryDisplay || "N/A"}</span>;
      },
    },
    {
      key: "ownershipDisplay",
      header: "Account Type",
      render: (val: any, row: any) => {
        return <span>{row.ownershipDisplay || row.ownership || "N/A"}</span>;
      },
    },
    {
      key: "meterDetails",
      header: "Utility Service",
      render: (val: any, row: any) => {
        const utilityService = `${row.meterDetails?.productCode || ""}`.trim();
        return <span>{utilityService || "N/A"}</span>;
      },
    },
    {
      key: "meterDetails",
      header: "Device No",
      render: (val: any, row: any) => {
        if (editingRowId === row.id) {
          return (
            <Input
              className="w-28 h-9 bg-white text-xs"
              value={editingRowData?.meterDetails?.deviceNo ?? ""}
              onChange={(e) =>
                setEditingRowData((prev: any) => ({
                  ...prev,
                  meterDetails: {
                    ...prev.meterDetails,
                    deviceNo: e.target.value,
                  },
                }))
              }
            />
          );
        }
        const deviceNo = `${row.meterDetails?.deviceNo || ""}`.trim();
        return <span>{deviceNo || "N/A"}</span>;
      },
    },
    {
      key: "meterDetails",
      header: "Initial Read",
      render: (val: any, row: any) => {
        const previousReading =
          `${row.meterDetails?.previousReading || ""}`.trim();
        return <span>{previousReading || "N/A"}</span>;
      },
    },
    {
      key: "currentReading",
      header: "Read",
      render: (val: any, row: any) => {
        if (editingRowId === row.id) {
          return (
            <Input
              className="w-24 h-9 bg-white text-center text-xs"
              placeholder="Enter"
              value={editingRowData?.currentReading ?? ""}
              onChange={(e) =>
                setEditingRowData((prev: any) => ({
                  ...prev,
                  currentReading: e.target.value,
                }))
              }
            />
          );
        }
        const readingValue = val ?? "";
        const isCollected = row.status === "Collected";
        return isCollected ? (
          <div className="bg-green-50 border border-green-200 rounded-md px-3 py-1 text-center font-medium text-green-700 w-24 text-xs mx-auto">
            {readingValue}
          </div>
        ) : (
          <span>{readingValue || "N/A"}</span>
        );
      },
    },
    {
      key: "readingDate",
      header: "Read Date",
      render: (val: any, row: any) => {
        if (editingRowId === row.id) {
          const rawDate = editingRowData?.readingDate ?? "";
          let formattedDate = rawDate;
          if (rawDate.includes("-") && rawDate.split("-")[2]?.length === 4) {
            const [m, d, y] = rawDate.split("-");
            formattedDate = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
          } else if (rawDate.includes("/") && rawDate.split("/")[2]?.length === 4) {
            const [m, d, y] = rawDate.split("/");
            formattedDate = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
          }
          return (
            <Input
              type="date"
              className="w-40 h-9 bg-white text-center text-xs"
              value={formattedDate}
              onChange={(e) =>
                setEditingRowData((prev: any) => ({
                  ...prev,
                  readingDate: e.target.value,
                }))
              }
            />
          );
        }
        return <span>{val || "N/A"}</span>;
      },
    },
    {
      key: "meterDetails",
      header: "Read Status",
      render: (val: any, row: any) => {
        if (editingRowId === row.id) {
          const currentValue =
            editingRowData?.meterDetails?.currentReadingMeterStatus ||
            editingRowData?.meterDetails?.currentReadingMeterStatusDisplay ||
            "";
          return (
            <GenericSelect
              value={currentValue}
              onChange={handleReadingStatusChange}
              options={meterReadingStatusOptions}
              placeholder="Select Read Status"
              className="h-9 min-w-[140px] text-xs bg-white"
              isSearchable={false}
            />
          );
        }
        const readingStatus =
          row.meterDetails?.currentReadingMeterStatusDisplay || "N/A";
        return (
          <Badge className={getStatusBadgeClass(readingStatus)}>
            {readingStatus}
          </Badge>
        );
      },
    },
    {
      key: "meterDetails",
      header: "Status",
      render: (val: any, row: any) => {
        const statusValue = row.meterDetails?.status || "N/A";
        return (
          <Badge className={getStatusBadgeClass(statusValue)}>
            {statusValue}
          </Badge>
        );
      },
    },
    {
      key: "id",
      header: "Action",
      render: (_, row) => {
        if (editingRowId === row.id) {
          return (
            <div className="flex items-center gap-1.5 justify-center">
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white text-xs h-8 px-2.5"
                onClick={handleSaveInline}
                disabled={!!savingId}
              >
                {savingId === row.id ? "Saving..." : "Save"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-8 px-2.5"
                onClick={handleCancelInline}
                disabled={!!savingId}
              >
                Cancel
              </Button>
            </div>
          );
        }
        return (
          <Button
            size="sm"
            variant="outline"
            className="border-indigo-600 text-indigo-600 hover:bg-indigo-50 text-xs h-8"
            onClick={() => handleStartEdit(row)}
            disabled={!!savingId || !!editingRowId}
          >
            Edit
          </Button>
        );
      },
    },
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Header section */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="p-2 bg-orange-50 rounded-lg">
            <Clock className="w-5 h-5 text-orange-600" />
          </div>
          <PageHeader title="Update Meter Activation Details" />
        </div>
        <p className="text-sm text-neutral-500 font-medium">
          {summaryData.totalReadingCollected} of {summaryData.totalConsumers}{" "}
          activated
        </p>
      </div>

      <div className="flex items-center gap-4">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv"
          className="hidden"
        />
        <Button
          variant="outline"
          onClick={handleUpload}
          disabled={isUploadingCSV}
        >
          <Upload className="w-4 h-4 mr-2" />
          {isUploadingCSV ? "Uploading..." : "Upload Activation CSV"}
        </Button>
        <Button
          variant="outline"
          onClick={handleDownload}
          disabled={isDownloading}
        >
          <Download className="w-4 h-4 mr-2" />
          {isDownloading ? "Downloading..." : "Download Template"}
        </Button>
        <span className="text-sm text-neutral-400">
          Or enter activation details manually below
        </span>
      </div>

      {/* KPI Section */}
      <div className="grid grid-cols-3 gap-4">
        <QuickStatsCard
          title="Total Consumers"
          count={summaryData.totalConsumers}
          icon={
            <div className="p-2 bg-blue-50 rounded-full">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
          }
        />
        <QuickStatsCard
          title="Meters Activated"
          count={summaryData.totalReadingCollected}
          icon={
            <div className="p-2 bg-green-50 rounded-full">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
          }
          className="bg-green-50/50"
        />
        <QuickStatsCard
          title="Pending Activation"
          count={summaryData.totalReadingPending}
          icon={
            <div className="p-2 bg-orange-50 rounded-full">
              <Clock className="w-5 h-5 text-orange-500" />
            </div>
          }
          className="bg-orange-50/50"
        />
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-xl shadow-sm border border-neutral-100 overflow-hidden p-2">
        <GenericTable
          data={localData}
          columns={columns}
          loading={isMeterListLoading}
          rowKey="id"
          emptyMessage="No meter activation data found"
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search by account no, name or device no..."
          filterPanel={
            <MeterActivationFilter
              form={filterForm}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          }
          getRowClassName={() =>
            "text-xs h-14 border-b last:border-0 hover:bg-indigo-50/20 transition-colors"
          }
        />
      </div>

      {/* Activate Consumer Button */}
      <div className="flex justify-end pt-4">
        <Button
          size="lg"
          onClick={handleActivateConsumer}
          disabled={!allMetersAssigned || isActivating}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold shadow-sm px-8"
        >
          {isActivating ? "Activating..." : "Activate Consumer"}
        </Button>
      </div>

      {/* Error Report Dialog */}
      <Dialog
        open={!!uploadErrorReport}
        onOpenChange={() => setUploadErrorReport(null)}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Upload Error Report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <p className="text-neutral-500 font-medium">
                Found{" "}
                <span className="font-bold text-red-600">
                  {uploadErrorReport?.error_count}
                </span>{" "}
                errors in the uploaded CSV.
              </p>
              <div className="px-3 py-1 bg-green-50 text-green-700 rounded-full font-medium">
                Successfully processed: {uploadErrorReport?.success_count}
              </div>
            </div>

            <div className="border rounded-xl overflow-hidden shadow-sm">
              <div className="max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-neutral-50 border-b sticky top-0">
                    <tr>
                      <th className="px-4 py-3 font-semibold text-neutral-600">
                        Row
                      </th>
                      <th className="px-4 py-3 font-semibold text-neutral-600">
                        Meter No
                      </th>
                      <th className="px-4 py-3 font-semibold text-neutral-600">
                        Error Message
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {uploadErrorReport?.errors?.map((err: any, idx: number) => (
                      <tr key={idx} className="hover:bg-neutral-50/50">
                        <td className="px-4 py-3 text-neutral-500 font-medium">
                          {err.row}
                        </td>
                        <td className="px-4 py-3 font-bold text-neutral-800">
                          {err.meter_no}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs font-medium border border-red-100">
                            {err.error}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeterActivationTab;
